// Core timeline calculation logic - extracted for testing
// This module exports pure functions that don't depend on the DOM

export function generateTasks(numTasks, taskConfigurations) {
    const tasks = [];

    for (let i = 0; i < numTasks; i++) {
        const taskId = i + 1;

        tasks.push({
            id: taskId,
            time: taskConfigurations[taskId]?.duration || 1,
            needsRework: taskConfigurations[taskId]?.needsRework || false,
            reworkPercent: (taskConfigurations[taskId]?.reworkTime || 30) / 100
        });
    }
    return tasks;
}

export function generateSoloTimeline(tasks, config = {}) {
    const reviewTime = config.reviewTime ?? 0.25;
    const contextSwitch = config.contextSwitch ?? 0.25;
    const startNewTask = config.startNewTask ?? false;

    const dev1Timeline = [];
    const dev2Timeline = [];
    let dev1Time = 0;
    let dev2Time = 0;

    let totalBlocked = 0;
    let totalContextSwitch = 0;
    let totalRework = 0;
    let totalReview = 0;
    let blockingEvents = 0;

    // Track what each dev was last doing (to know when to context switch)
    let dev1LastActivity = null; // 'work-X', 'review-X', 'rework-X', or 'blocked'
    let dev2LastActivity = null;

    // Helper to add context switch if needed
    function addContextSwitchIfNeeded(dev, newActivity, timeline, devTime) {
        const lastActivity = dev === 1 ? dev1LastActivity : dev2LastActivity;

        // Context switch needed if:
        // 1. Not the first activity
        // 2. Last activity wasn't blocked (after blocking you just start next thing)
        // 3. New activity is different from last
        if (lastActivity !== null && lastActivity !== 'blocked' && lastActivity !== newActivity) {
            timeline.push({
                type: 'context-switch',
                label: 'Switch task',
                duration: contextSwitch,
                start: devTime
            });
            totalContextSwitch += contextSwitch;
            return devTime + contextSwitch;
        }
        return devTime;
    }

    // Split tasks between developers
    const dev1Tasks = tasks.filter((_, i) => i % 2 === 0);
    const dev2Tasks = tasks.filter((_, i) => i % 2 === 1);

    // Common queue-based approach for both modes
    let dev1TaskIdx = 0;
    let dev2TaskIdx = 0;
    const pendingReviews = [];
    const pendingRework = [];
    const taskReviewRounds = new Map(); // Track review rounds per task
    const maxReviewRounds = 3;

    // Helper to determine if task needs rework on this review round
    function needsReworkOnReview(needsRework, reviewRound) {
        // Only allow rework on the first review round for deterministic behavior
        if (reviewRound === 1) return needsRework;
        return false; // No rework on subsequent rounds
    }

    if (!startNewTask) {
        // Block while waiting for review - process in lockstep
        for (let i = 0; i < Math.max(dev1Tasks.length, dev2Tasks.length); i++) {
            const roundStartTime = Math.max(dev1Time, dev2Time);

            // Dev 1 works on their task
            if (i < dev1Tasks.length) {
                const task = dev1Tasks[i];
                const activityId = `work-${task.id}`;
                taskReviewRounds.set(task.id, 0);

                dev1Time = addContextSwitchIfNeeded(1, activityId, dev1Timeline, dev1Time);
                dev1LastActivity = activityId;

                dev1Timeline.push({
                    type: 'work',
                    label: `Task ${task.id}`,
                    duration: task.time,
                    start: dev1Time
                });
                dev1Time += task.time;

                // Add to pending reviews
                pendingReviews.push({
                    taskId: task.id,
                    taskTime: task.time,
                    needsRework: task.needsRework,
                    reworkPercent: task.reworkPercent,
                    reviewer: 2,
                    author: 1,
                    readyAt: dev1Time,
                    reviewRound: 1
                });
            }

            // Dev 2 works on their task
            if (i < dev2Tasks.length) {
                const task = dev2Tasks[i];
                const activityId = `work-${task.id}`;
                taskReviewRounds.set(task.id, 0);

                dev2Time = addContextSwitchIfNeeded(2, activityId, dev2Timeline, dev2Time);
                dev2LastActivity = activityId;

                dev2Timeline.push({
                    type: 'work',
                    label: `Task ${task.id}`,
                    duration: task.time,
                    start: dev2Time
                });
                dev2Time += task.time;

                // Add to pending reviews
                pendingReviews.push({
                    taskId: task.id,
                    taskTime: task.time,
                    needsRework: task.needsRework,
                    reworkPercent: task.reworkPercent,
                    reviewer: 1,
                    author: 2,
                    readyAt: dev2Time,
                    reviewRound: 1
                });
            }

            // Process reviews and rework in chronological order (by readyAt time)
            // This prevents unnecessary blocking when a developer could be doing other work
            while (pendingReviews.length > 0 || pendingRework.length > 0) {
                // Merge and sort pending items by readyAt time and which developer can do them
                const allPending = [
                    ...pendingRework.map(r => ({ type: 'rework', data: r, readyAt: r.readyAt, doer: r.author })),
                    ...pendingReviews.map(r => ({ type: 'review', data: r, readyAt: r.readyAt, doer: r.reviewer }))
                ];

                // Sort by: 1) which developer is available first, 2) readyAt time
                allPending.sort((a, b) => {
                    const aDevTime = a.doer === 1 ? dev1Time : dev2Time;
                    const bDevTime = b.doer === 1 ? dev1Time : dev2Time;
                    const aEffectiveTime = Math.max(aDevTime, a.readyAt);
                    const bEffectiveTime = Math.max(bDevTime, b.readyAt);
                    return aEffectiveTime - bEffectiveTime;
                });

                if (allPending.length === 0) break;

                const next = allPending[0];

                if (next.type === 'rework') {
                    const rework = next.data;
                    // Remove from pendingRework
                    const reworkIndex = pendingRework.findIndex(r => r === rework);
                    if (reworkIndex >= 0) pendingRework.splice(reworkIndex, 1);
                    const reworkActivityId = `rework-${rework.taskId}-r${rework.reviewRound}`;

                    if (rework.author === 1) {
                        // Block until rework ready
                        if (dev1Time < rework.readyAt) {
                            const waitTime = rework.readyAt - dev1Time;
                            dev1Timeline.push({
                                type: 'blocked',
                                label: 'Waiting',
                                duration: waitTime,
                                start: dev1Time
                            });
                            dev1Time = rework.readyAt;
                            totalBlocked += waitTime;
                            dev1LastActivity = 'blocked';
                        }

                        dev1Time = addContextSwitchIfNeeded(1, reworkActivityId, dev1Timeline, dev1Time);
                        dev1LastActivity = reworkActivityId;

                        dev1Timeline.push({
                            type: 'rework',
                            label: `Rework ${rework.taskId}`,
                            duration: rework.reworkTime,
                            start: dev1Time
                        });
                        dev1Time += rework.reworkTime;
                        totalRework += rework.reworkTime;

                        // Add back to reviews
                        pendingReviews.push({
                            taskId: rework.taskId,
                            taskTime: rework.taskTime,
                            needsRework: rework.needsRework,
                            reworkPercent: rework.reworkPercent,
                            reviewer: 2,
                            author: 1,
                            readyAt: dev1Time,
                            reviewRound: rework.reviewRound + 1
                        });
                    } else {
                        // Block until rework ready
                        if (dev2Time < rework.readyAt) {
                            const waitTime = rework.readyAt - dev2Time;
                            dev2Timeline.push({
                                type: 'blocked',
                                label: 'Waiting',
                                duration: waitTime,
                                start: dev2Time
                            });
                            dev2Time = rework.readyAt;
                            totalBlocked += waitTime;
                            dev2LastActivity = 'blocked';
                        }

                        dev2Time = addContextSwitchIfNeeded(2, reworkActivityId, dev2Timeline, dev2Time);
                        dev2LastActivity = reworkActivityId;

                        dev2Timeline.push({
                            type: 'rework',
                            label: `Rework ${rework.taskId}`,
                            duration: rework.reworkTime,
                            start: dev2Time
                        });
                        dev2Time += rework.reworkTime;
                        totalRework += rework.reworkTime;

                        // Add back to reviews
                        pendingReviews.push({
                            taskId: rework.taskId,
                            taskTime: rework.taskTime,
                            needsRework: rework.needsRework,
                            reworkPercent: rework.reworkPercent,
                            reviewer: 1,
                            author: 2,
                            readyAt: dev2Time,
                            reviewRound: rework.reviewRound + 1
                        });
                    }
                } else {
                    // Process review
                    const review = next.data;
                    // Remove from pendingReviews
                    const reviewIndex = pendingReviews.findIndex(r => r === review);
                    if (reviewIndex >= 0) pendingReviews.splice(reviewIndex, 1);
                const reviewActivityId = `review-${review.taskId}-r${review.reviewRound}`;

                if (review.reviewer === 1) {
                    // Block until review ready
                    if (dev1Time < review.readyAt) {
                        const waitTime = review.readyAt - dev1Time;
                        dev1Timeline.push({
                            type: 'blocked',
                            label: 'Waiting',
                            duration: waitTime,
                            start: dev1Time
                        });
                        dev1Time = review.readyAt;
                        totalBlocked += waitTime;
                        blockingEvents++;
                        dev1LastActivity = 'blocked';
                    }

                    dev1Time = addContextSwitchIfNeeded(1, reviewActivityId, dev1Timeline, dev1Time);
                    dev1LastActivity = reviewActivityId;

                    dev1Timeline.push({
                        type: 'review',
                        label: `Review ${review.taskId}`,
                        duration: reviewTime,
                        start: dev1Time
                    });
                    dev1Time += reviewTime;
                    totalReview += reviewTime;

                    // Check if rework needed
                    if (needsReworkOnReview(review.needsRework, review.reviewRound)) {
                        const reworkTime = review.taskTime * review.reworkPercent;
                        pendingRework.push({
                            taskId: review.taskId,
                            taskTime: review.taskTime,
                            needsRework: review.needsRework,
                            reworkPercent: review.reworkPercent,
                            reworkTime: reworkTime,
                            author: review.author,
                            readyAt: dev1Time,
                            reviewRound: review.reviewRound
                        });
                    }
                } else {
                    // Block until review ready
                    if (dev2Time < review.readyAt) {
                        const waitTime = review.readyAt - dev2Time;
                        dev2Timeline.push({
                            type: 'blocked',
                            label: 'Waiting',
                            duration: waitTime,
                            start: dev2Time
                        });
                        dev2Time = review.readyAt;
                        totalBlocked += waitTime;
                        blockingEvents++;
                        dev2LastActivity = 'blocked';
                    }

                    dev2Time = addContextSwitchIfNeeded(2, reviewActivityId, dev2Timeline, dev2Time);
                    dev2LastActivity = reviewActivityId;

                    dev2Timeline.push({
                        type: 'review',
                        label: `Review ${review.taskId}`,
                        duration: reviewTime,
                        start: dev2Time
                    });
                    dev2Time += reviewTime;
                    totalReview += reviewTime;

                    // Check if rework needed
                    if (needsReworkOnReview(review.needsRework, review.reviewRound)) {
                        const reworkTime = review.taskTime * review.reworkPercent;
                        pendingRework.push({
                            taskId: review.taskId,
                            taskTime: review.taskTime,
                            needsRework: review.needsRework,
                            reworkPercent: review.reworkPercent,
                            reworkTime: reworkTime,
                            author: review.author,
                            readyAt: dev2Time,
                            reviewRound: review.reviewRound
                        });
                    }
                }
                }
            }
        }
    } else {
        // Non-blocking mode: can start new task while waiting for review
        while (dev1TaskIdx < dev1Tasks.length || dev2TaskIdx < dev2Tasks.length ||
               pendingReviews.length > 0 || pendingRework.length > 0) {

            // Determine what each developer can do next
            let nextDev1Action = null;
            let nextDev2Action = null;

            // Check for rework first (highest priority)
            const dev1Rework = pendingRework.find(r => r.author === 1 && dev1Time >= r.readyAt);
            const dev2Rework = pendingRework.find(r => r.author === 2 && dev2Time >= r.readyAt);

            // Check for reviews
            const dev1Review = pendingReviews.find(r => r.reviewer === 1 && dev1Time >= r.readyAt);
            const dev2Review = pendingReviews.find(r => r.reviewer === 2 && dev2Time >= r.readyAt);

            // Parallel review detection
            let handleParallelReviews = false;
            if (dev1Review && dev2Review && Math.abs(dev1Time - dev2Time) < 0.01) {
                // Both devs ready for review at same time - process in parallel
                handleParallelReviews = true;

                // Dev 1 review
                const reviewActivityId1 = `review-${dev1Review.taskId}-r${dev1Review.reviewRound}`;
                dev1Time = addContextSwitchIfNeeded(1, reviewActivityId1, dev1Timeline, dev1Time);
                dev1LastActivity = reviewActivityId1;

                dev1Timeline.push({
                    type: 'review',
                    label: `Review ${dev1Review.taskId}`,
                    duration: reviewTime,
                    start: dev1Time
                });
                dev1Time += reviewTime;
                totalReview += reviewTime;

                // Check if rework needed
                if (needsReworkOnReview(dev1Review.needsRework, dev1Review.reviewRound)) {
                    const reworkTime = dev1Review.taskTime * dev1Review.reworkPercent;
                    pendingRework.push({
                        taskId: dev1Review.taskId,
                        taskTime: dev1Review.taskTime,
                        needsRework: dev1Review.needsRework,
                        reworkPercent: dev1Review.reworkPercent,
                        reworkTime: reworkTime,
                        author: dev1Review.author,
                        readyAt: dev1Time,
                        reviewRound: dev1Review.reviewRound
                    });
                }
                pendingReviews.splice(pendingReviews.indexOf(dev1Review), 1);

                // Dev 2 review
                const reviewActivityId2 = `review-${dev2Review.taskId}-r${dev2Review.reviewRound}`;
                dev2Time = addContextSwitchIfNeeded(2, reviewActivityId2, dev2Timeline, dev2Time);
                dev2LastActivity = reviewActivityId2;

                dev2Timeline.push({
                    type: 'review',
                    label: `Review ${dev2Review.taskId}`,
                    duration: reviewTime,
                    start: dev2Time
                });
                dev2Time += reviewTime;
                totalReview += reviewTime;

                // Check if rework needed
                if (needsReworkOnReview(dev2Review.needsRework, dev2Review.reviewRound)) {
                    const reworkTime = dev2Review.taskTime * dev2Review.reworkPercent;
                    pendingRework.push({
                        taskId: dev2Review.taskId,
                        taskTime: dev2Review.taskTime,
                        needsRework: dev2Review.needsRework,
                        reworkPercent: dev2Review.reworkPercent,
                        reworkTime: reworkTime,
                        author: dev2Review.author,
                        readyAt: dev2Time,
                        reviewRound: dev2Review.reviewRound
                    });
                }
                pendingReviews.splice(pendingReviews.indexOf(dev2Review), 1);
            }

            if (!handleParallelReviews) {
                // Determine dev1's next action
                if (dev1Rework) {
                    nextDev1Action = { type: 'rework', data: dev1Rework };
                } else if (dev1Review) {
                    nextDev1Action = { type: 'review', data: dev1Review };
                } else if (dev1TaskIdx < dev1Tasks.length) {
                    nextDev1Action = { type: 'work', data: dev1Tasks[dev1TaskIdx] };
                }

                // Determine dev2's next action
                if (dev2Rework) {
                    nextDev2Action = { type: 'rework', data: dev2Rework };
                } else if (dev2Review) {
                    nextDev2Action = { type: 'review', data: dev2Review };
                } else if (dev2TaskIdx < dev2Tasks.length) {
                    nextDev2Action = { type: 'work', data: dev2Tasks[dev2TaskIdx] };
                }

                // Process the earlier developer's action
                const processDev1 = nextDev1Action && (!nextDev2Action || dev1Time <= dev2Time);
                const processDev2 = nextDev2Action && (!nextDev1Action || dev2Time < dev1Time);

                if (processDev1) {
                    const action = nextDev1Action;

                    if (action.type === 'rework') {
                        const rework = action.data;
                        const reworkActivityId = `rework-${rework.taskId}-r${rework.reviewRound}`;
                        dev1Time = addContextSwitchIfNeeded(1, reworkActivityId, dev1Timeline, dev1Time);
                        dev1LastActivity = reworkActivityId;

                        dev1Timeline.push({
                            type: 'rework',
                            label: `Rework ${rework.taskId}`,
                            duration: rework.reworkTime,
                            start: dev1Time
                        });
                        dev1Time += rework.reworkTime;
                        totalRework += rework.reworkTime;

                        // Add back to reviews
                        pendingReviews.push({
                            taskId: rework.taskId,
                            taskTime: rework.taskTime,
                            needsRework: rework.needsRework,
                            reworkPercent: rework.reworkPercent,
                            reviewer: 2,
                            author: 1,
                            readyAt: dev1Time,
                            reviewRound: rework.reviewRound + 1
                        });
                        pendingRework.splice(pendingRework.indexOf(rework), 1);
                    } else if (action.type === 'review') {
                        const review = action.data;
                        const reviewActivityId = `review-${review.taskId}-r${review.reviewRound}`;
                        dev1Time = addContextSwitchIfNeeded(1, reviewActivityId, dev1Timeline, dev1Time);
                        dev1LastActivity = reviewActivityId;

                        dev1Timeline.push({
                            type: 'review',
                            label: `Review ${review.taskId}`,
                            duration: reviewTime,
                            start: dev1Time
                        });
                        dev1Time += reviewTime;
                        totalReview += reviewTime;

                        // Check if rework needed
                        if (needsReworkOnReview(review.needsRework, review.reviewRound)) {
                            const reworkTime = review.taskTime * review.reworkPercent;
                            pendingRework.push({
                                taskId: review.taskId,
                                taskTime: review.taskTime,
                                needsRework: review.needsRework,
                                reworkPercent: review.reworkPercent,
                                reworkTime: reworkTime,
                                author: review.author,
                                readyAt: dev1Time,
                                reviewRound: review.reviewRound
                            });
                        }
                        pendingReviews.splice(pendingReviews.indexOf(review), 1);
                    } else if (action.type === 'work') {
                        const task = action.data;
                        const activityId = `work-${task.id}`;
                        dev1Time = addContextSwitchIfNeeded(1, activityId, dev1Timeline, dev1Time);
                        dev1LastActivity = activityId;

                        dev1Timeline.push({
                            type: 'work',
                            label: `Task ${task.id}`,
                            duration: task.time,
                            start: dev1Time
                        });
                        dev1Time += task.time;

                        // Add to pending reviews
                        pendingReviews.push({
                            taskId: task.id,
                            taskTime: task.time,
                            needsRework: task.needsRework,
                            reworkPercent: task.reworkPercent,
                            reviewer: 2,
                            author: 1,
                            readyAt: dev1Time,
                            reviewRound: 1
                        });
                        dev1TaskIdx++;
                    }
                } else if (processDev2) {
                    const action = nextDev2Action;

                    if (action.type === 'rework') {
                        const rework = action.data;
                        const reworkActivityId = `rework-${rework.taskId}-r${rework.reviewRound}`;
                        dev2Time = addContextSwitchIfNeeded(2, reworkActivityId, dev2Timeline, dev2Time);
                        dev2LastActivity = reworkActivityId;

                        dev2Timeline.push({
                            type: 'rework',
                            label: `Rework ${rework.taskId}`,
                            duration: rework.reworkTime,
                            start: dev2Time
                        });
                        dev2Time += rework.reworkTime;
                        totalRework += rework.reworkTime;

                        // Add back to reviews
                        pendingReviews.push({
                            taskId: rework.taskId,
                            taskTime: rework.taskTime,
                            needsRework: rework.needsRework,
                            reworkPercent: rework.reworkPercent,
                            reviewer: 1,
                            author: 2,
                            readyAt: dev2Time,
                            reviewRound: rework.reviewRound + 1
                        });
                        pendingRework.splice(pendingRework.indexOf(rework), 1);
                    } else if (action.type === 'review') {
                        const review = action.data;
                        const reviewActivityId = `review-${review.taskId}-r${review.reviewRound}`;
                        dev2Time = addContextSwitchIfNeeded(2, reviewActivityId, dev2Timeline, dev2Time);
                        dev2LastActivity = reviewActivityId;

                        dev2Timeline.push({
                            type: 'review',
                            label: `Review ${review.taskId}`,
                            duration: reviewTime,
                            start: dev2Time
                        });
                        dev2Time += reviewTime;
                        totalReview += reviewTime;

                        // Check if rework needed
                        if (needsReworkOnReview(review.needsRework, review.reviewRound)) {
                            const reworkTime = review.taskTime * review.reworkPercent;
                            pendingRework.push({
                                taskId: review.taskId,
                                taskTime: review.taskTime,
                                needsRework: review.needsRework,
                                reworkPercent: review.reworkPercent,
                                reworkTime: reworkTime,
                                author: review.author,
                                readyAt: dev2Time,
                                reviewRound: review.reviewRound
                            });
                        }
                        pendingReviews.splice(pendingReviews.indexOf(review), 1);
                    } else if (action.type === 'work') {
                        const task = action.data;
                        const activityId = `work-${task.id}`;
                        dev2Time = addContextSwitchIfNeeded(2, activityId, dev2Timeline, dev2Time);
                        dev2LastActivity = activityId;

                        dev2Timeline.push({
                            type: 'work',
                            label: `Task ${task.id}`,
                            duration: task.time,
                            start: dev2Time
                        });
                        dev2Time += task.time;

                        // Add to pending reviews
                        pendingReviews.push({
                            taskId: task.id,
                            taskTime: task.time,
                            needsRework: task.needsRework,
                            reworkPercent: task.reworkPercent,
                            reviewer: 1,
                            author: 2,
                            readyAt: dev2Time,
                            reviewRound: 1
                        });
                        dev2TaskIdx++;
                    }
                } else {
                    // No immediate action available - check if we need to wait for pending items
                    if (pendingReviews.length > 0 || pendingRework.length > 0) {
                        // Find the earliest time when an action becomes available
                        const nextTimes = [];

                        if (pendingReviews.length > 0) {
                            const earliestReview = pendingReviews.reduce((earliest, review) =>
                                review.readyAt < earliest.readyAt ? review : earliest
                            );
                            nextTimes.push(earliestReview.readyAt);
                        }

                        if (pendingRework.length > 0) {
                            const earliestRework = pendingRework.reduce((earliest, rework) =>
                                rework.readyAt < earliest.readyAt ? rework : earliest
                            );
                            nextTimes.push(earliestRework.readyAt);
                        }

                        const nextTime = Math.min(...nextTimes);

                        // Advance the earlier developer to the next available time
                        if (dev1Time <= dev2Time && dev1Time < nextTime) {
                            const waitTime = nextTime - dev1Time;
                            dev1Timeline.push({
                                type: 'blocked',
                                label: 'Waiting',
                                duration: waitTime,
                                start: dev1Time
                            });
                            dev1Time = nextTime;
                            totalBlocked += waitTime;
                            blockingEvents++;
                            dev1LastActivity = 'blocked';
                        } else if (dev2Time < nextTime) {
                            const waitTime = nextTime - dev2Time;
                            dev2Timeline.push({
                                type: 'blocked',
                                label: 'Waiting',
                                duration: waitTime,
                                start: dev2Time
                            });
                            dev2Time = nextTime;
                            totalBlocked += waitTime;
                            blockingEvents++;
                            dev2LastActivity = 'blocked';
                        }
                    } else {
                        // No pending work - exit loop
                        break;
                    }
                }
            }
        }
    }

    const totalTime = Math.max(dev1Time, dev2Time);

    return {
        dev1: dev1Timeline,
        dev2: dev2Timeline,
        totalTime: totalTime,
        waste: {
            blocked: parseFloat(totalBlocked.toFixed(2)),
            contextSwitch: parseFloat(totalContextSwitch.toFixed(2)),
            rework: parseFloat(totalRework.toFixed(2)),
            review: parseFloat(totalReview.toFixed(2)),
            total: parseFloat((totalBlocked + totalReview + totalRework).toFixed(2)),
            blockingEvents: blockingEvents
        }
    };
}

export function generatePairTimeline(tasks, config = {}) {
    const contextSwitch = config.contextSwitch ?? 0.25;
    const pairEfficiency = config.pairEfficiency ?? 30;

    const timeline = [];
    let currentTime = 0;

    for (let i = 0; i < tasks.length; i++) {
        const task = tasks[i];

        // Add context switch between tasks (except for first task)
        if (i > 0) {
            timeline.push({
                type: 'context-switch',
                label: 'Switch task',
                duration: contextSwitch,
                start: currentTime
            });
            currentTime += contextSwitch;
        }

        // Apply pair efficiency gain to task time
        const pairTaskTime = task.time * (1 - pairEfficiency / 100);

        timeline.push({
            type: 'work',
            label: `Task ${task.id}`,
            duration: pairTaskTime,
            start: currentTime
        });
        currentTime += pairTaskTime;
    }

    return {
        timeline: timeline,
        totalTime: currentTime
    };
}
